import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Container, Table, Button, InputGroup, Form, Pagination, Row, Col, Badge, Alert,
} from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import ProtectedSection from '../components/ProtectedSection';
import exchangeService from '../services/exchangeService';
import dailyRateService from '../services/dailyRateService';
import ExchangeModal from '../components/ExchangeModal';
import ExchangeViewModal from '../components/ExchangeViewModal';
import ReceiptPrintButton from '../components/ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

const badgeVariant = (status) => status === 'COMPLETED' ? 'success' : 'secondary';
const typeLabel = (type) => {
  const t = String(type || '').toUpperCase();
  if (t === 'BUY') return 'Achat';
  if (t === 'SELL') return 'Vente';
  return '—';
};

export default function ExchangePage() {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('EXCHANGE_VIEW');
  const canUpdate = hasRight('EXCHANGE_UPDATE');
  const canDelete = hasRight('EXCHANGE_DELETE');

  const initialDates = useMemo(() => defaultDateRange(), []);
  const [rows, setRows] = useState([]);
  const [rate, setRate] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewShow, setViewShow] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fetchRate = useCallback(() => {
    dailyRateService.get().then(setRate).catch(() => setRate(null));
  }, []);

  const fetchAll = useCallback(() => {
    if (!canView) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) {
      setErrorMsg(dateError); setRows([]); setTotal(0); return;
    }
    setErrorMsg('');
    exchangeService.list({ page, pageSize, search: filter, type: typeFilter, dateFrom, dateTo })
      .then((r) => {
        setRows(r?.data || []);
        setTotal(Number(r?.total || 0));
        const pages = Math.max(1, Number(r?.totalPages || Math.ceil(Number(r?.total || 0) / pageSize)));
        if (page > pages) setPage(pages);
      })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur chargement Exchange.'));
  }, [canView, page, pageSize, filter, typeFilter, dateFrom, dateTo]);

  useEffect(() => { fetchRate(); }, [fetchRate]);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setPage(1); }, [filter, typeFilter, dateFrom, dateTo, pageSize]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const openCreate = () => { setEditRow(null); setShowModal(true); };
  const openEdit = (r) => { setEditRow(r); setShowModal(true); };
  const openView = (r) => {
    if (!r?.id) return;
    setViewShow(true); setViewRow(null); setViewError(''); setViewLoading(true);
    exchangeService.get(r.id).then(setViewRow)
      .catch((e) => setViewError(e?.response?.data?.message || 'Erreur chargement détails.'))
      .finally(() => setViewLoading(false));
  };
  const closeView = () => { setViewShow(false); setViewRow(null); setViewError(''); setViewLoading(false); };
  const save = (payload) => {
    setErrorMsg('');
    const op = editRow?.id ? exchangeService.update(editRow.id, payload) : exchangeService.create(payload);
    op.then(() => { setShowModal(false); setEditRow(null); fetchAll(); fetchRate(); })
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur enregistrement Exchange.'));
  };
  const remove = (r) => {
    if (!window.confirm('Supprimer cette transaction Exchange ?')) return;
    setErrorMsg('');
    exchangeService.remove(r.id).then(fetchAll)
      .catch((e) => setErrorMsg(e?.response?.data?.message || 'Erreur suppression.'));
  };

  const buildReceiptDocument = async (r) => {
    const full = await exchangeService.get(r.id);
    const clientLabel = full?.client
      ? `${full.client.firstName || ''} ${full.client.lastName || ''}`.trim()
      : (full?.clientName || 'Non-client');
    const username = full?.createdByUser?.username || '—';
    return {
      title: `Reçu Exchange${full?.id ? ` - #${full.id}` : ''}`,
      rows: [
        { label: 'ID opération', value: full?.id || '—' },
        { label: 'Type', value: typeLabel(full?.type) },
        { label: 'Client / bénéficiaire', value: clientLabel },
        { label: 'Statut', value: full?.status || '—' },
        { label: 'Montant USD', value: `${Number(full?.amountUSD || 0).toFixed(2)} USD` },
        { label: 'Taux utilisé', value: `${Number(full?.rateUsdHtg || 0).toFixed(2)} HTG` },
        { label: 'Montant HTG', value: `${Number(full?.amountHTG || 0).toFixed(2)} HTG` },
        { label: 'Créée le', value: full?.createdAt ? new Date(full.createdAt).toLocaleString() : '—' },
        { label: 'Utilisateur', value: username },
      ],
      signatures: { client: 'Signature client', cashier: username },
    };
  };

  const buyRate = Number(rate?.buyRateUsdHtg ?? rate?.usdToHtg ?? 0);
  const sellRate = Number(rate?.sellRateUsdHtg ?? rate?.usdToHtg ?? 0);

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2>Exchange System</h2>
        <ProtectedSection code="EXCHANGE_CREATE"><Button onClick={openCreate}>+ Nouvelle opération</Button></ProtectedSection>
      </div>
      <Alert variant="secondary" className="mb-3">
        <div><strong>Taux achat :</strong> {buyRate > 0 ? `1 USD = ${buyRate.toFixed(2)} HTG` : 'Non défini'}</div>
        <div><strong>Taux vente :</strong> {sellRate > 0 ? `1 USD = ${sellRate.toFixed(2)} HTG` : 'Non défini'}</div>
      </Alert>
      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      <Row className="g-2 mb-3 align-items-end">
        <Col md={3}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
        <Col md={3}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
        <Col md={6}><Form.Label>Recherche</Form.Label><InputGroup><Form.Control placeholder="Client, type, note..." value={filter} onChange={(e) => setFilter(e.target.value)} /><Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button></InputGroup></Col>
        <Col md={6}><Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tous types</option><option value="BUY">Achat</option><option value="SELL">Vente</option></Form.Select></Col>
        <Col md={3}><Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 / page</option><option value={20}>20 / page</option><option value={50}>50 / page</option><option value={100}>100 / page</option></Form.Select></Col>
      </Row>

      <ProtectedSection code="EXCHANGE_VIEW">
        <Table hover responsive>
          <thead><tr><th>Type</th><th>Client / bénéficiaire</th><th className="text-end">USD</th><th className="text-end">Taux</th><th className="text-end">HTG</th><th>Statut</th><th>Date</th><th style={{ width: 360 }}>Actions</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const clientLabel = r.client ? `${r.client.firstName || ''} ${r.client.lastName || ''}`.trim() : (r.clientName || 'Non-client');
              return <tr key={r.id}>
                <td><Badge bg={String(r.type).toUpperCase() === 'BUY' ? 'info' : 'warning'} text="dark">{typeLabel(r.type)}</Badge></td>
                <td>{clientLabel}</td><td className="text-end">{Number(r.amountUSD).toFixed(2)}</td><td className="text-end">{Number(r.rateUsdHtg).toFixed(2)}</td><td className="text-end">{Number(r.amountHTG).toFixed(2)}</td>
                <td><Badge bg={badgeVariant(r.status)}>{r.status}</Badge></td><td>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</td>
                <td>
                  <ProtectedSection code="EXCHANGE_VIEW"><Button variant="outline-info" size="sm" className="me-2 mb-1" onClick={() => openView(r)}>Voir</Button></ProtectedSection>
                  <ReceiptPrintButton className="me-2 mb-1" getDocument={() => buildReceiptDocument(r)} />
                  {canUpdate && <Button variant="outline-secondary" size="sm" className="me-2 mb-1" onClick={() => openEdit(r)}>Modifier</Button>}
                  {canDelete && <Button variant="outline-danger" size="sm" className="mb-1" onClick={() => remove(r)}>Supprimer</Button>}
                </td>
              </tr>;
            })}
          </tbody>
        </Table>
        {rows.length === 0 && <p className="text-center text-muted">Aucune transaction Exchange.</p>}
        <div className="d-flex justify-content-between align-items-center mt-3"><div className="text-muted">Résultats : {total} • Page {page}/{totalPages}</div><Pagination className="mb-0">{paginationItems}</Pagination></div>
      </ProtectedSection>

      <ExchangeModal show={showModal} onHide={() => { setShowModal(false); setEditRow(null); }} onSave={save} initialData={editRow} currentRates={rate} />
      <ExchangeViewModal show={viewShow} onHide={closeView} row={viewRow} loading={viewLoading} error={viewError} />
    </Container>
  );
}
